import { i18n } from '@lingui/core';
import { CHATWOOT_BASE_URL, CHATWOOT_WEBSITE_TOKEN } from '@/base/constants';

type ChatwootUser = {
  name?: string;
  email?: string;
  phone_number?: string;
  avatar_url?: string;
  // Signed by the inbox HMAC token; without it Chatwoot trusts the identifier
  // as-is, so the inbox must keep identity validation disabled.
  identifier_hash?: string;
};

type Chatwoot = {
  toggle: (state: 'open' | 'close') => void;
  setLocale: (locale: string) => void;
  setUser: (identifier: string, user: ChatwootUser) => void;
  reset: () => void;
  isOpen: boolean;
};

type ChatwootWindow = Window & {
  $chatwoot?: Chatwoot;
  chatwootSettings?: Record<string, unknown>;
  chatwootSDK?: {
    run: (options: { websiteToken: string; baseUrl: string }) => void;
  };
};

const chatwootWindow = () => window as ChatwootWindow;

let chatwootPromise: Promise<Chatwoot | null> | null = null;

export const initiateChatwoot = () => {
  if (!chatwootPromise) {
    const baseUrl = CHATWOOT_BASE_URL;
    const websiteToken = CHATWOOT_WEBSITE_TOKEN;
    chatwootPromise = new Promise<Chatwoot | null>((resolve) => {
      if (!baseUrl || !websiteToken) return resolve(null);
      const w = chatwootWindow();
      w.chatwootSettings = {
        position: document.documentElement.dir === 'rtl' ? 'left' : 'right',
        locale: i18n.locale,
        showUnreadMessagesDialog: false,
      };
      // `$chatwoot` is only exposed once the SDK boots its iframe, which
      // happens after the script's own `load` event.
      window.addEventListener(
        'chatwoot:ready',
        () => resolve(w.$chatwoot ?? null),
        { once: true },
      );
      window.addEventListener('chatwoot:error', () => resolve(null), {
        once: true,
      });
      const s = document.createElement('script');
      s.src = `${baseUrl}/packs/js/sdk.js`;
      s.id = 'chatwoot-sdk';
      s.async = true;
      s.onload = () => w.chatwootSDK?.run({ websiteToken, baseUrl });
      s.onerror = () => resolve(null);
      (document.body ?? document.head).appendChild(s);
    });
  }
  return chatwootPromise;
};

export const openChatwoot = async () => {
  const chatwoot = await initiateChatwoot();
  chatwoot?.toggle('open');
};
